import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { GroupedSegment } from "@/utils/group-segments";
import { ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface ChatSearchProps {
    segments: GroupedSegment[];
    onMessageClick?: (timestamp: number) => void;
    onSearchTermChange?: (searchTerm: string) => void;
    onHighlightedMessageChange?: (timestamp: number | null) => void;
    className?: string;
}

interface SearchResult {
    group: GroupedSegment;
    groupIndex: number;
    matches: {
        text: string;
        startIndex: number;
        endIndex: number;
    }[];
}

export function ChatSearch({ segments, onMessageClick, onSearchTermChange, onHighlightedMessageChange, className }: ChatSearchProps) {
    const [searchQuery, setSearchQuery] = useState("");
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [isSearchOpen, setIsSearchOpen] = useState(false);

    const searchResults = useMemo(() => {
        if (!searchQuery.trim()) return [];

        const query = searchQuery.toLowerCase();
        const results: SearchResult[] = [];

        segments.forEach((group, groupIndex) => {
            const fullText = group.texts.join(" ").toLowerCase();
            const matches: { text: string; startIndex: number; endIndex: number }[] = [];

            let startIndex = 0;
            while (true) {
                const index = fullText.indexOf(query, startIndex);
                if (index === -1) break;

                matches.push({
                    text: group.texts.join(" "),
                    startIndex: index,
                    endIndex: index + query.length
                });
                startIndex = index + 1;
            }

            if (matches.length > 0) {
                results.push({
                    group,
                    groupIndex,
                    matches
                });
            }
        });

        return results;
    }, [segments, searchQuery]);

    const totalMatches = searchResults.reduce((total, result) => total + result.matches.length, 0);

    useEffect(() => {
        setCurrentMatchIndex(0);
    }, [searchQuery]);

    useEffect(() => {
        if (onSearchTermChange) {
            onSearchTermChange(searchQuery);
        }
    }, [searchQuery, onSearchTermChange]);

    useEffect(() => {
        if (!isSearchOpen && onHighlightedMessageChange) {
            onHighlightedMessageChange(null);
        }
    }, [isSearchOpen, onHighlightedMessageChange]);

    const handleNextMatch = () => {
        if (currentMatchIndex < totalMatches - 1) {
            setCurrentMatchIndex(currentMatchIndex + 1);
        }
    };

    const handlePreviousMatch = () => {
        if (currentMatchIndex > 0) {
            setCurrentMatchIndex(currentMatchIndex - 1);
        }
    };

    const scrollToCurrentMatch = () => {
        if (searchResults.length === 0) {
            if (onHighlightedMessageChange) {
                onHighlightedMessageChange(null);
            }
            return;
        }

        let matchCount = 0;
        let targetGroup: GroupedSegment | null = null;

        for (const result of searchResults) {
            if (matchCount + result.matches.length > currentMatchIndex) {
                targetGroup = result.group;
                break;
            }
            matchCount += result.matches.length;
        }

        if (targetGroup && onMessageClick) {
            onMessageClick(targetGroup.startTime);

            if (onHighlightedMessageChange) {
                onHighlightedMessageChange(targetGroup.startTime);
            }

            // Scroll to the message
            const messageElement = document.getElementById(`message-time-${Math.floor(targetGroup.startTime)}`);
            if (messageElement) {
                messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    };

    useEffect(() => {
        if (totalMatches > 0) {
            scrollToCurrentMatch();
        }
    }, [currentMatchIndex, totalMatches]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            if (e.shiftKey) {
                handlePreviousMatch();
            } else {
                handleNextMatch();
            }
        } else if (e.key === "Escape") {
            setIsSearchOpen(false);
            setSearchQuery("");
        }
    };

    if (!isSearchOpen) {
        return (
            <Button variant="outline" onClick={() => setIsSearchOpen(true)} className={cn("h-9 gap-2 font-normal text-muted-foreground", className)}>
                <Search className="h-4 w-4" />
                Search within conversation
            </Button>
        );
    }

    return (
        <div className={cn("flex h-9 items-center gap-2 rounded-md border bg-background px-2", className)}>
            <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search..."
                    className="h-7 border-0 bg-transparent pr-8 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                    autoFocus
                />
                {searchQuery && (
                    <Button variant="ghost" size="sm" onClick={() => setSearchQuery("")} className="absolute right-1 top-1/2 h-5 w-5 -translate-y-1/2 p-0">
                        <X className="h-3 w-3" />
                    </Button>
                )}
            </div>

            {searchQuery && totalMatches > 0 && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <span>{currentMatchIndex + 1}</span>
                    <span>/</span>
                    <span>{totalMatches}</span>
                </div>
            )}

            {searchQuery && totalMatches > 0 && (
                <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={handlePreviousMatch} disabled={currentMatchIndex === 0} className="h-7 w-7 p-0">
                        <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleNextMatch} disabled={currentMatchIndex === totalMatches - 1} className="h-7 w-7 p-0">
                        <ChevronDown className="h-4 w-4" />
                    </Button>
                </div>
            )}

            <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                    setIsSearchOpen(false);
                    setSearchQuery("");
                }}
                className="h-7 w-7 p-0"
            >
                <X className="h-4 w-4" />
            </Button>
        </div>
    );
}
